import { DestroyRef, Injectable, Injector, WritableSignal, effect, inject, signal } from '@angular/core';
import { NativeWorkbuddyAgentChatAdapter } from './native.workbuddy.agent.chat.adapter';
import { AgentUtility } from '../agent.utility';
import type { Signal } from '@angular/core';
import type { AnyPart } from '../agent.types';

/**
 * Manages smooth text reveal animation for streamed chat messages.
 *
 * Progressively reveals text/reasoning parts using requestAnimationFrame,
 * converting chunky SSE updates into smooth character-by-character rendering.
 *
 * Provided at component level (same as AgentChatService).
 */
@Injectable()
export class AgentRevealService {

  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);

  // ─── State ──────────────────────────────────────────────────────────────────

  /**
   * Trailing characters held back when the live tail part is first sighted, so
   * typing visibly resumes the moment a backgrounded stream is reattached
   * instead of sitting still until the next network chunk arrives.
   */
  private static readonly TAIL_RUNWAY_CHARS = 120;

  /**
   * Maximum un-revealed backlog a part may accumulate before the revealed text
   * snaps forward. requestAnimationFrame freezes in background tabs while the
   * stream keeps growing the targets, so without this clamp a return after
   * minutes away typewriters the entire backlog from where the user left off.
   */
  private static readonly MAX_ANIMATION_BACKLOG_CHARS = 480;

  private initialized = false;
  private revealFrameId: number | null = null;
  private readonly revealTargets = new Map<string, string>();

  public readonly $revealedTextParts: WritableSignal<Record<string, string>> = signal<Record<string, string>>({});

  // ─── Lifecycle ──────────────────────────────────────────────────────────────

  /**
   * Start monitoring an adapter signal for text changes and animate reveals.
   * Call once after the chat adapter is created.
   */
  public init($chat: Signal<NativeWorkbuddyAgentChatAdapter>): void {

    if (this.initialized) return;
    this.initialized = true;
    this.destroyRef.onDestroy(() => this.stopRevealLoop());

    effect(() => {

      const chat = $chat();
      const streaming = chat.status === 'streaming' || chat.status === 'submitted';
      const textParts = this.collectRenderableTextParts(chat);
      const nextKeys = new Set(textParts.map(({ key }) => key));

      if (!streaming) {
        this.stopRevealLoop();
        this.revealTargets.clear();
        this.$revealedTextParts.set(Object.fromEntries(textParts.map(({ key, text }) => [key, text])));
        return;
      }

      const tailKey = textParts.length > 0 ? textParts[textParts.length - 1].key : null;

      this.$revealedTextParts.update((current) => {

        const next: Record<string, string> = {};
        for (const { key, text } of textParts) {
          const existing = current[key];
          if (existing === undefined) {
            // First sighting of this part — snap to its current content. Only growth
            // observed while watching animates. Without this, reattaching a backgrounded
            // stream (or restoring a thread mid-turn) re-typewrites the entire
            // conversation from scratch because the revealed state was wiped on detach.
            // The tail part keeps a short runway to animate so the resume is visible.
            next[key] = key === tailKey ? text.slice(0, Math.max(0, text.length - AgentRevealService.TAIL_RUNWAY_CHARS)) : text;
          } else {
            const base = text.startsWith(existing) ? existing : text;
            // Clamp the animation debt: with the loop frozen in a background tab the
            // backlog grows without bound, so cap it at a short catch-up window —
            // non-tail parts snap straight to their full content, the tail keeps its
            // runway so typing visibly resumes from (nearly) live position.
            const backlog = text.length - base.length;
            next[key] = backlog > AgentRevealService.MAX_ANIMATION_BACKLOG_CHARS
              ? (key === tailKey ? text.slice(0, Math.max(0, text.length - AgentRevealService.TAIL_RUNWAY_CHARS)) : text)
              : base;
          }
          this.revealTargets.set(key, text);
        }
        return next;
      });

      for (const key of [...this.revealTargets.keys()]) {
        if (!nextKeys.has(key)) this.revealTargets.delete(key);
      }

      this.startRevealLoop();
    }, { injector: this.injector });
  }

  /**
   * Reset all reveal state. Call when switching threads.
   */
  public reset(): void {

    this.stopRevealLoop();
    this.revealTargets.clear();
    this.$revealedTextParts.set({});
  }

  // ─── Private ────────────────────────────────────────────────────────────────

  private collectRenderableTextParts(chat: NativeWorkbuddyAgentChatAdapter): Array<{ key: string; text: string }> {

    const parts: Array<{ key: string; text: string }> = [];

    for (const message of chat.messages) {
      if (message.role !== 'assistant') continue;

      for (let i = 0; i < message.parts.length; i++) {
        const part = message.parts[i];
        if (part.type !== 'text' && part.type !== 'reasoning') continue;
        parts.push({ key: `${message.id}-${i}`, text: AgentUtility.partField(part as AnyPart, 'text', '') });
      }
    }

    return parts;
  }

  private startRevealLoop(): void {

    if (this.revealFrameId !== null) return;

    const tick = () => {

      let hasPending = false;

      this.$revealedTextParts.update((current) => {

        const next = { ...current };
        let changed = false;

        for (const [key, target] of this.revealTargets) {
          const visible = next[key] ?? '';

          if (visible === target) continue;

          hasPending = true;
          changed = true;
          const remaining = target.slice(visible.length);
          const chunkSize = remaining.length > 160 ? 4 : remaining.length > 60 ? 2 : 1;
          next[key] = target.slice(0, visible.length + chunkSize);
          // Reveal one part at a time, in document order — the earlier part finishes
          // typing before the next begins. Advancing every pending key per frame made
          // a lagging thinking block and the answer text typewrite simultaneously.
          break;
        }

        return changed ? next : current;
      });

      if (hasPending) {
        this.revealFrameId = requestAnimationFrame(tick);
      } else {
        this.revealFrameId = null;
      }
    };

    this.revealFrameId = requestAnimationFrame(tick);
  }

  private stopRevealLoop(): void {

    if (this.revealFrameId !== null) {
      cancelAnimationFrame(this.revealFrameId);
      this.revealFrameId = null;
    }
  }
}
